import { CheckboxGroup, type CheckboxGroupResult } from '@myosh/odin-components';
import { useReactFlow } from '@xyflow/react';
import { type Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useUpdateGeneratedDiagramFiltersMutation,
  useUpdateStoredDiagramFiltersMutation,
} from '../../../api/enhanced/enhanced-bowtie-api';
import { useDiagramContext } from '../../../context/diagram.context';
import useDiagramFilterData from '../../../hooks/use-diagram-filter-data';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectShowControlEffectiveness,
  selectShowControlsInSingleRow,
  selectShowControlTypes,
  selectShowOnlyCriticalControls,
  setFilter,
} from '../../../redux/slices/diagram';

interface DisplayFilterTabProps {
  ref?: Ref<DisplayFilterTabRef>;
}

export interface DisplayFilterTabRef {
  save: () => void;
}

type FilterKey =
  | 'showOnlyCriticalControls'
  | 'showControlTypes'
  | 'showControlEffectiveness'
  | 'showControlsInSingleRow';

type FilterState = Record<FilterKey, boolean>;

interface FilterOption {
  [key: string]: unknown;
  value: FilterKey;
  text: string;
}

const FILTER_OPTIONS: Array<FilterOption> = [
  { value: 'showOnlyCriticalControls', text: 'Show only Critical Controls' },
  { value: 'showControlTypes', text: 'Show Control Types' },
  { value: 'showControlEffectiveness', text: 'Show Control Effectiveness' },
  { value: 'showControlsInSingleRow', text: 'Show Controls in a Single Row' },
];

const toSelectedOptions = (filters: FilterState): Array<CheckboxGroupResult> =>
  FILTER_OPTIONS.filter((option) => filters[option.value]);

const toFilterState = (selected: Array<CheckboxGroupResult> | undefined): FilterState => ({
  showOnlyCriticalControls: !!selected?.some((item) => item.value === 'showOnlyCriticalControls'),
  showControlTypes: !!selected?.some((item) => item.value === 'showControlTypes'),
  showControlEffectiveness: !!selected?.some((item) => item.value === 'showControlEffectiveness'),
  showControlsInSingleRow: !!selected?.some((item) => item.value === 'showControlsInSingleRow'),
});

function DisplayFilterTab({ ref }: DisplayFilterTabProps) {
  const showOnlyCriticalControls = useAppSelector(selectShowOnlyCriticalControls);
  const showControlTypes = useAppSelector(selectShowControlTypes);
  const showControlEffectiveness = useAppSelector(selectShowControlEffectiveness);
  const showControlsInSingleRow = useAppSelector(selectShowControlsInSingleRow);

  const [selected, setSelected] = useState<Array<CheckboxGroupResult>>(() =>
    toSelectedOptions({
      showOnlyCriticalControls,
      showControlTypes,
      showControlEffectiveness,
      showControlsInSingleRow,
    })
  );

  const dispatch = useAppDispatch();
  const { fitView } = useReactFlow();

  const { diagramId, recordId } = useDiagramContext();
  const { diagramFilterData, loading } = useDiagramFilterData();

  const [updateStoredDiagramFilters, { isLoading: isUpdatingStoredDiagramFilters }] =
    useUpdateStoredDiagramFiltersMutation();

  const [updateGeneratedDiagramFilters, { isLoading: isUpdatingGeneratedDiagramFilters }] =
    useUpdateGeneratedDiagramFiltersMutation();

  // Hydrate both the working copy and Redux when the filter data arrives from the server.
  useEffect(() => {
    if (!loading && !isUpdatingGeneratedDiagramFilters && !isUpdatingStoredDiagramFilters && diagramFilterData) {
      const loaded: FilterState = {
        showOnlyCriticalControls: diagramFilterData.showOnlyCriticalControls,
        showControlTypes: diagramFilterData.showControlTypes,
        showControlEffectiveness: diagramFilterData.showControlEffectiveness,
        showControlsInSingleRow: diagramFilterData.showControlsInSingleRow,
      };
      setSelected(toSelectedOptions(loaded));
      dispatch(setFilter(loaded));
    }
  }, [diagramFilterData, loading, isUpdatingGeneratedDiagramFilters, isUpdatingStoredDiagramFilters, dispatch]);

  const applyClickHandler = useCallback(() => {
    const filters = toFilterState(selected);
    dispatch(setFilter(filters));
    setTimeout(fitView, 0);

    let updateFilters: typeof updateStoredDiagramFilters | typeof updateGeneratedDiagramFilters | undefined;
    let id: string | undefined;

    if (recordId) {
      id = recordId;
      updateFilters = updateStoredDiagramFilters;
    } else if (diagramId) {
      id = diagramId;
      updateFilters = updateGeneratedDiagramFilters;
    }

    if (updateFilters && id) {
      toast.promise(
        updateFilters({
          id,
          diagramFilterDto: { displayFilter: filters },
        })
          .unwrap()
          .finally(() => setTimeout(fitView, 0)),
        {
          pending: 'Saving filters...',
          success: { render: 'Filters saved', autoClose: 1200, type: 'info' },
          error: 'Unable to save filters',
        }
      );
    }
  }, [selected, dispatch, fitView, recordId, diagramId, updateStoredDiagramFilters, updateGeneratedDiagramFilters]);

  useImperativeHandle(ref, () => ({
    save: applyClickHandler,
  }));

  return (
    <CheckboxGroup
      name="displayFilters"
      data={FILTER_OPTIONS}
      textField="text"
      valueField="value"
      value={selected}
      onChange={(value) => setSelected(value ?? [])}
    />
  );
}

export default DisplayFilterTab;
